import { useCallback } from "react"

/**
 * Custom hook for managing breadcrumbs with navigation callbacks
 *
 * @param {Object} contentLoader - Content loader state and functions
 * @param {Function} navigateTo - Navigation function
 * @returns {Object} Enhanced breadcrumb items with navigation handlers
 */
export function useBreadcrumbs(contentLoader, navigateTo) {
  const { breadcrumbItems, expandedCategories, setExpandedCategories } =
    contentLoader

  /**
   * Enhances breadcrumb items with click handlers
   */
  const enhancedBreadcrumbs = useCallback(() => {
    return breadcrumbItems.map((item) => {
      // Skip items without links
      if (!item.link) return item

      // For the root item (Help Center)
      if (item.label === "Help Center") {
        return {
          ...item,
          onClick: () => navigateTo(null),
        }
      }

      // For category breadcrumbs
      if (item.categorySlug) {
        return {
          ...item,
          onClick: () => {
            // Preserve expanded state when clicking breadcrumb
            const updatedExpandedCategories = { ...expandedCategories }

            // If we have parent path information, ensure those categories stay expanded
            if (item.parentPath) {
              const parentCategories = item.parentPath.split("/")
              parentCategories.forEach((cat) => {
                updatedExpandedCategories[cat] = true
              })
            }

            // Ensure the current category is expanded
            updatedExpandedCategories[item.categorySlug] = true

            setExpandedCategories(updatedExpandedCategories)

            // Navigate to the category
            navigateTo("category", item.categorySlug, item.parentPath || "")
          },
        }
      }

      // Return unchanged for other items
      return item
    })
  }, [breadcrumbItems, expandedCategories, navigateTo, setExpandedCategories])

  return {
    breadcrumbs: enhancedBreadcrumbs(),
  }
}

export default useBreadcrumbs
